// PersonalManagerArea.ts

import { registerPageInit } from '../barba/page-init-registry';
import '../../css/_root.scss';
import '../../css/pages/personal-area/personal-area.scss';
import '../../css/pages/personal-area/personal-area-table.scss';
import { TabWidget } from '../widgets/tab-widget';

import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

import Button from 'primevue/button';

import PrimeVue from 'primevue/config';
import Aura from '@primeuix/themes/aura';
import { createApp } from 'vue';
import Toast from 'primevue/toast';

import ConfirmDialog from 'primevue/confirmdialog';

import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';

import 'primeicons/primeicons.css';

import RequestsContent from '@/js/widgets/manager-residency-requests/index.vue';
import { initPasswordChangeModal } from '../widgets/password-change-modal';
import { initLogoutConfirmModal } from '../widgets/logout-confirm-modal';
import { initLkMobileNotice } from '../widgets/lk-mobile-notice';
import { getBitrixSessid } from '../utils/getBitrixSessid';
import {
  showStcErrorNotification,
  showStcSuccessNotification,
} from '@/js/components/stc-notification';
import { getApiUrl } from '../utils/getApiUrl';

class PersonalManagerArea {
  private static readonly SETTINGS_FORM_SELECTOR = '#residentSettingsForm';
  private static readonly SETTINGS_FORM_BOUND_ATTR = 'residentSettingsFormBound';
  private managerTabWidget: TabWidget;
  private readonly settingsSubmitUrl: string;
  private isSettingsSubmitting: boolean = false;

  constructor() {
    this.managerTabWidget = new TabWidget({
      rootSelector: '.lk-page',
    });

    this.settingsSubmitUrl = getApiUrl('/api/form/settings/');
  }

  public init(root?: ParentNode): void {
    this.bindEvents();
    initLogoutConfirmModal(root);
    initLkMobileNotice(root);
    const queryTab = this.getTabIdFromUrl();
    this.managerTabWidget.init(queryTab ?? undefined, (tabId: number | undefined) => {
      console.log('tabId', tabId);
    });
    initPasswordChangeModal(root);
    const appElementSelector = '#lk-manager-requests-list';
    const appElement = document.querySelector(appElementSelector);

    if (appElement) {
      const app = createApp(RequestsContent);
      app.use(PrimeVue, {
        theme: {
          preset: Aura,
          options: {
            // Отключает автоопределение темы
            darkModeSelector: false,
          },
        },
      });

      app.component('Button', Button);
      app.component('DataTable', DataTable);
      app.component('Column', Column);
      app.component('ConfirmDialog', ConfirmDialog);
      app.component('Toast', Toast);

      app.use(ToastService);
      app.use(ConfirmationService);

      app.mount(appElementSelector);
    }
  }

  private bindEvents(): void {
    const settingsForm = document.querySelector<HTMLFormElement>(
      PersonalManagerArea.SETTINGS_FORM_SELECTOR,
    );
    if (!settingsForm) {
      return;
    }

    if (settingsForm.dataset[PersonalManagerArea.SETTINGS_FORM_BOUND_ATTR] === '1') {
      return;
    }

    const handleChange = async (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') {
        return;
      }

      await this.submitSettingsAsJson(settingsForm);
    };

    settingsForm.addEventListener('change', handleChange);
    settingsForm.dataset[PersonalManagerArea.SETTINGS_FORM_BOUND_ATTR] = '1';
  }

  private getSettingsCheckboxValue(form: HTMLFormElement, names: string[]): boolean {
    for (const name of names) {
      const input = form.querySelector<HTMLInputElement>(`input[name="${name}"]`);
      if (input) {
        return input.checked;
      }
    }

    return false;
  }

  private async submitSettingsAsJson(form: HTMLFormElement): Promise<void> {
    if (this.isSettingsSubmitting) {
      return;
    }

    const sessid = getBitrixSessid();
    if (!sessid) {
      console.error('Не удалось получить сессию пользователя');
      return;
    }

    const notifyManagerMessage = this.getSettingsCheckboxValue(form, [
      'notify_manager_message',
      'notify_new_message',
    ]);
    const notifyStatusReport = this.getSettingsCheckboxValue(form, ['notify_status_report']);

    this.isSettingsSubmitting = true;
    try {
      const response = await fetch(this.settingsSubmitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Bitrix-Csrf-Token': sessid,
        },
        body: JSON.stringify({
          notify_manager_message: notifyManagerMessage ? 1 : 0,
          notify_status_report: notifyStatusReport ? 1 : 0,
          sessid,
        }),
      });

      const result = (await response.json()) as {
        msg?: string;
        status?: 'success' | 'error';
      };

      if (!response.ok || result.status === 'error') {
        showStcErrorNotification(result.msg || 'Ошибка обработки');
        return;
      }

      showStcSuccessNotification(result.msg || 'Данные успешно сохранены');
    } catch {
      showStcErrorNotification('Ошибка соединения');
    } finally {
      this.isSettingsSubmitting = false;
    }
  }

  private getTabIdFromUrl(): number | null {
    const params = new URLSearchParams(window.location.search);
    const tabId = params.get('tab');
    return tabId ? Number(tabId) : null;
  }
}

registerPageInit('personal-manager-area', (root?: ParentNode) => {
  const page = new PersonalManagerArea();
  page.init(root);
});
